import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type PlaySequenceOptions = {
  onComplete?: () => void;
};

type NarrationPlayer = {
  audioRef: MutableRefObject<HTMLAudioElement | null>;
  isSupported: boolean;
  playSequence: (sources: string[], options?: PlaySequenceOptions) => void;
  stop: () => void;
  unlock: () => void;
  playback: {
    src: string | null;
    currentTime: number;
    duration: number;
    isPlaying: boolean;
  };
};

export const useNarrationPlayer = (mutedRef: MutableRefObject<boolean>): NarrationPlayer => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioSequenceIdRef = useRef(0);
  const [isSupported, setIsSupported] = useState(false);
  const [playback, setPlayback] = useState({
    src: null as string | null,
    currentTime: 0,
    duration: 0,
    isPlaying: false,
  });

  useEffect(() => {
    if (!playback.isPlaying) return;
    const intervalId = window.setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      setPlayback((prev) => ({
        ...prev,
        currentTime: audio.currentTime,
        duration: Number.isFinite(audio.duration) ? audio.duration : prev.duration,
        isPlaying: !audio.paused,
      }));
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [playback.isPlaying]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsSupported(typeof Audio !== 'undefined');
    return () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
      audio.onerror = null;
    };
  }, []);

  const stop = useCallback(() => {
    audioSequenceIdRef.current += 1;
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    audio.onended = null;
    audio.onerror = null;
    setPlayback({ src: null, currentTime: 0, duration: 0, isPlaying: false });
  }, []);

  const playSequence = useCallback(
    (sources: string[], options?: PlaySequenceOptions) => {
      if (typeof window === 'undefined' || sources.length === 0) return;
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = 'auto';
      }
      const audio = audioRef.current;
      const sequenceId = audioSequenceIdRef.current + 1;
      audioSequenceIdRef.current = sequenceId;
      let index = 0;
      let isHandlingEnd = false;
      let progressTimer: number | null = null;
      const clearProgressTimer = () => {
        if (progressTimer !== null) {
          window.clearInterval(progressTimer);
          progressTimer = null;
        }
      };
      const cleanupListeners = (
        onEnded: () => void,
        onError: () => void,
        onMeta: () => void,
        onTimeUpdate: () => void,
        onPause: () => void
      ) => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
        audio.removeEventListener('loadedmetadata', onMeta);
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('pause', onPause);
        clearProgressTimer();
      };
      const playNext = () => {
        if (sequenceId !== audioSequenceIdRef.current) return;
        if (index >= sources.length) {
          options?.onComplete?.();
          return;
        }
        const src = sources[index];
        const handleEnded = () => {
          if (isHandlingEnd) return;
          isHandlingEnd = true;
          audio.onended = null;
          audio.onerror = null;
          setPlayback((prev) => ({ ...prev, isPlaying: false }));
          cleanupListeners(handleEnded, handleError, handleMeta, handleTimeUpdate, handlePause);
          index += 1;
          isHandlingEnd = false;
          playNext();
        };
        const handleError = () => {
          if (isHandlingEnd) return;
          isHandlingEnd = true;
          audio.onended = null;
          audio.onerror = null;
          setPlayback((prev) => ({ ...prev, isPlaying: false }));
          cleanupListeners(handleEnded, handleError, handleMeta, handleTimeUpdate, handlePause);
          index += 1;
          isHandlingEnd = false;
          playNext();
        };
        const handleMeta = () => {
          if (!Number.isFinite(audio.duration)) return;
          setPlayback((prev) => ({
            ...prev,
            duration: Number.isFinite(audio.duration) ? audio.duration : 0,
          }));
        };
        const handleTimeUpdate = () => {
          if (isHandlingEnd) return;
          if (!Number.isFinite(audio.duration)) return;
          setPlayback((prev) => ({
            ...prev,
            currentTime: audio.currentTime,
          }));
          if (audio.currentTime >= Math.max(audio.duration - 0.05, 0)) {
            handleEnded();
          }
        };
        const handlePause = () => {
          if (isHandlingEnd) return;
          if (!Number.isFinite(audio.duration)) return;
          setPlayback((prev) => ({ ...prev, isPlaying: !audio.paused }));
          if (audio.currentTime >= Math.max(audio.duration - 0.05, 0)) {
            handleEnded();
          }
        };
        audio.src = src;
        audio.currentTime = 0;
        audio.volume = mutedRef.current ? 0 : 1;
        audio.onended = handleEnded;
        audio.onerror = handleError;
        audio.addEventListener('ended', handleEnded, { once: true });
        audio.addEventListener('error', handleError, { once: true });
        audio.addEventListener('loadedmetadata', handleMeta, { once: true });
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('pause', handlePause);
        setPlayback({
          src,
          currentTime: 0,
          duration: Number.isFinite(audio.duration) ? audio.duration : 0,
          isPlaying: true,
        });
        let lastTime = -1;
        let stalledTicks = 0;
        progressTimer = window.setInterval(() => {
          if (isHandlingEnd) return;
          if (audio.ended) {
            handleEnded();
            return;
          }
          const hasDuration = Number.isFinite(audio.duration);
          if (hasDuration && audio.currentTime >= Math.max(audio.duration - 0.05, 0)) {
            handleEnded();
            return;
          }
          if (lastTime > 0.5 && audio.currentTime < 0.1) {
            handleEnded();
            return;
          }
          if (audio.currentTime === lastTime) {
            if (audio.currentTime > 0.5) {
              stalledTicks += 1;
            }
          } else {
            stalledTicks = 0;
            lastTime = audio.currentTime;
          }
          if (stalledTicks >= 3) {
            handleEnded();
          }
        }, 1000);
        audio
          .play()
          .catch(() => {
            handleError();
          });
      };
      playNext();
    },
    [mutedRef]
  );

  const unlock = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
    }
    const audio = audioRef.current;
    if (!audio || audio.src) return;
    // iOS Safari requires a user-gesture play to unlock audio.
    audio.src =
      'data:audio/mp3;base64,SUQzAwAAAAAAFlRFTkMAAAABAAABAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgI';
    audio.volume = 0;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = mutedRef.current ? 0 : 1;
      })
      .catch(() => {});
  }, [mutedRef]);

  return {
    audioRef,
    isSupported,
    playSequence,
    stop,
    unlock,
    playback,
  };
};
